import 'mapbox-gl-leaflet'

import { mapboxGL, tileLayer } from 'leaflet'

const baseTileOptions = {
    maxZoom: 20,
    minZoom: 2,
    traceBorderColor: null,
    traceColor: '#000',
}

const state = {
    addressCoordinates: null,
    forceAlign: false,
    heatmapData: null,
    lastUserPosition: null,
    mapInstance: null,
    navigationRoute: null,
    positionTrackingEnabled: false,
    savedBounds: null,
    tileProviders: [
        {
            id: 'standard',
            createLayer: () =>
                tileLayer(
                    'https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}' +
                        '?access_token=' +
                        process.env.VUE_APP_MAPBOX_TOKEN,
                    { maxZoom: 20, tileSize: 512, zoomOffset: -1 }
                ),
            ...baseTileOptions,
            traceColor: '#13365e',
        },
        {
            id: 'outdoor',
            createLayer: () =>
                tileLayer(
                    'https://api.mapbox.com/styles/v1/mapbox/outdoors-v11/tiles/{z}/{x}/{y}' +
                        '?access_token=' +
                        process.env.VUE_APP_MAPBOX_TOKEN,
                    { maxZoom: 20, tileSize: 512, zoomOffset: -1 }
                ),
            ...baseTileOptions,
        },
        {
            id: 'street',
            createLayer: () =>
                tileLayer(
                    'https://api.mapbox.com/styles/v1/mapbox/navigation-preview-day-v4/tiles/{z}/{x}/{y}' +
                        '?access_token=' +
                        process.env.VUE_APP_MAPBOX_TOKEN,
                    { maxZoom: 20, tileSize: 512, zoomOffset: -1 }
                ),
            ...baseTileOptions,
        },
        {
            id: 'satellite',
            createLayer: () =>
                tileLayer(
                    'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/{z}/{x}/{y}' +
                        '?access_token=' +
                        process.env.VUE_APP_MAPBOX_TOKEN,
                    { maxZoom: 20, tileSize: 512, zoomOffset: -1 }
                ),
            ...baseTileOptions,
            traceBorderColor: '#000',
            traceColor: '#03fc07',
        },
        {
            id: 'swisstopo',
            createLayer: () =>
                tileLayer(
                    'https://wmts20.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857/{z}/{x}/{y}.jpeg',
                    { maxZoom: 20, tileSize: 256 }
                ),
            ...baseTileOptions,
            traceBorderColor: '#000',
            traceColor: '#03fc07',
        },
        {
            id: 'swisstopo-hiking',
            createLayer: () =>
                tileLayer(
                    'https://wmts20.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe/default/current/3857/{z}/{x}/{y}.jpeg',
                    { maxZoom: 19, tileSize: 256 }
                ),
            ...baseTileOptions,
            maxZoom: 19,
            traceBorderColor: '#000',
            traceColor: '#03fc07',
        },
        {
            id: 'vectormap',
            createLayer: () =>
                mapboxGL({
                    accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
                    style: 'mapbox://styles/mapbox/light-v10?optimize=true',
                }),
            ...baseTileOptions,
            maxZoom: 23,
            traceBorderColor: '#516677',
            traceColor: '#a4c7f5',
        },
        {
            id: 'vectormap-dark',
            createLayer: () =>
                mapboxGL({
                    accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
                    style: 'mapbox://styles/mapbox/dark-v10?optimize=true',
                }),
            ...baseTileOptions,
            maxZoom: 23,
            traceBorderColor: '#516677',
            traceColor: '#a4c7f5',
        },
    ],
}

const getters = {
    activeTileProvider(state, getters, rootState, rootGetters) {
        const providerId =
            rootGetters['preferences/mapDisplayPreferences']
                .activeTileProviderId
        return (
            state.tileProviders.find(({ id }) => id === providerId) ||
            getters.defaultTileProvider
        )
    },
    defaultTileProvider(state) {
        return process.env.VUE_APP_DEFAULT_MAP
            ? state.tileProviders.find(
                  ({ id }) => id === process.env.VUE_APP_DEFAULT_MAP
              ) || state.tileProviders[0]
            : state.tileProviders[0]
    },
    liveUpdatesEnabledConditionally(state, getters, rootState, rootGetters) {
        return rootGetters['tracker/isTrackerCountHigh']
            ? rootGetters['preferences/mapDisplayPreferences']
                  .liveUpdatesEnabled
            : true
    },
    simpleMarkersEnabledConditionally(state, getters, rootState, rootGetters) {
        return rootGetters['tracker/isTrackerCountHigh']
            ? rootGetters['preferences/mapDisplayPreferences']
                  .simpleMarkersEnabled
            : false
    },
}

const actions = {}

const mutations = {
    addHeatmapData(state, data) {
        if (!state.heatmapData) {
            state.heatmapData = []
        }

        state.heatmapData.push(...data)
    },
    clearSavedBounds(state) {
        state.savedBounds = null
    },
    resetHeatmapData(state) {
        state.heatmapData = null
    },
    saveBounds(state, data) {
        state.savedBounds = data
    },
    setMapInstance(state, data) {
        state.mapInstance = data
    },
    setNavigationRoute(state, data) {
        state.navigationRoute = data
    },
    setPositionTrackingEnabled(state, data) {
        state.positionTrackingEnabled = data
    },
    setUserPosition(state, data) {
        state.lastUserPosition = data
    },
    setForceAlign(state, data) {
        state.forceAlign = data
    },
    setAddressCoordinates(state, data) {
        state.addressCoordinates = data
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
